import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '@super/shared-types'
import {
  Agent,
  AgentVoiceSettings,
  AgentEmailSettings,
  ApiResponse,
  AgentTemplatesResponse,
  AgentDirection,
  AgentChannel,
  CreateAgentTriggerDto,
  DialOutSettings,
  UpsertDialOutSettingsDto,
  type Skill,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

type SkillInput = Pick<Skill, 'type'> & {
  params?: Record<string, unknown> | null
}

export interface CreateAgentRequest {
  name: string
  companyId?: string
  direction?: AgentDirection
  channels?: AgentChannel[]
  metadata?: Record<string, unknown>
  voiceSettings: Omit<
    AgentVoiceSettings,
    'id' | 'agentId' | 'createdAt' | 'updatedAt'
  >
  emailSettings?: Omit<
    AgentEmailSettings,
    'id' | 'agentId' | 'createdAt' | 'updatedAt'
  >
  knowledgeBaseIds?: string[]
  workflowIds?: string[]
  skills?: SkillInput[]
  agentTrigger?: CreateAgentTriggerDto | null
  dialOutSettings?: UpsertDialOutSettingsDto | null
}

export interface UpdateAgentRequest {
  name?: string
  active?: boolean
  direction?: AgentDirection
  channels?: AgentChannel[]
  metadata?: Record<string, unknown>
  voiceSettings?: Partial<
    Omit<AgentVoiceSettings, 'id' | 'agentId' | 'createdAt' | 'updatedAt'>
  >
  emailSettings?: Partial<
    Omit<AgentEmailSettings, 'id' | 'agentId' | 'createdAt' | 'updatedAt'>
  >
  knowledgeBaseIds?: string[]
  workflowIds?: string[]
  skills?: SkillInput[] | null
  agentTrigger?: CreateAgentTriggerDto | null
  dialOutSettings?: UpsertDialOutSettingsDto | null
}

export interface AgentWithVoiceSettings extends Agent {
  voiceSettings?: AgentVoiceSettings
  emailSettings?: AgentEmailSettings
  knowledgeBaseIds?: string[]
  workflowIds?: string[]
  skills?: Skill[]
  agentTriggers?: import('@super/shared-types').AgentTrigger[]
  dialOutSettings?: DialOutSettings | null
}

export class AgentsApiClient extends BaseApiClient {
  /**
   * Get available agent templates
   * @returns List of agent templates for configuration
   */
  async getTemplates(): Promise<AgentTemplatesResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentTemplatesResponse>
        >(`${API_ROUTES.AGENTS.GET}/templates`)
        return response.data || null
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching agent templates',
      },
    )
  }

  /**
   * Get all agents for current user's company
   * @returns Array of agents with voice settings
   */
  async getCompanyAgents(): Promise<AgentWithVoiceSettings[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings[]>
        >(API_ROUTES.AGENTS.GET)
        return response.data || []
      },
      {
        on404: { fallback: [] },
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching company agents',
      },
    )
    return result || []
  }

  /**
   * Create agent (for current user's company)
   * @param data - The data to create the agent with
   * @returns The created agent with voice settings
   */
  async createAgent(
    data: CreateAgentRequest,
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(API_ROUTES.AGENTS.CREATE, {
          method: 'POST',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating agent',
      },
    )
  }

  /**
   * Update agent (for current user's company)
   * @param id - The ID of the agent to update
   * @param data - The data to update the agent with
   * @returns The updated agent with voice settings
   */
  async updateAgent(
    id: string,
    data: UpdateAgentRequest,
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(interpolateRoute(API_ROUTES.AGENTS.UPDATE, { id }), {
          method: 'PUT',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating agent',
      },
    )
  }

  /**
   * Delete agent (soft delete - for current user's company)
   * @param id - The ID of the agent to delete
   * @returns Success status
   */
  async deleteAgent(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request<ApiResponse<{ success: boolean }>>(
          interpolateRoute(API_ROUTES.AGENTS.DELETE, { id }),
          {
            method: 'DELETE',
          },
        )
        return true
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting agent',
      },
    )
    return result ?? false
  }
}

export const agentsApiClient = new AgentsApiClient()
